import type { AuthenticatedUser, FeatureAccessOptions } from '@blueshift-ui/auth';
import type { PropsWithChildren } from 'react';

import * as styles from './index.css';
import * as translations from '../../../constants/translations';
import { NavLink, Outlet, ScrollRestoration, useRouteError } from 'react-router-dom';
import FLAGS from '../../../constants/flags';
import SettingsNavPanel from '../../../components/navigation/settings-nav-panel';
import SiteNav from '@blueshift-ui/navigation/dist/components/site-nav';
import { Suspense } from 'react';
import { clsx } from 'clsx';
import isMobileDevice from '../../../helpers/is-mobile-device';
import removeBasePath from '@blueshift-ui/navigation/dist/helpers/remove-base-path';
import useActiveLearner from './hooks/use-active-learner';
import useDebounceTrackMenu from './hooks/use-debounce-track-menu';
import useFeatureAccess from '@blueshift-ui/auth/dist/hooks/use-feature-access';
import useIsVt4sStudent from '../../../hooks/use-is-vt4s-student';
import useNavSlug from './hooks/use-nav-slug';
import usePageViewTracking from './hooks/use-page-view-tracking';
import useTranslation from '@blueshift-ui/i18n/dist/hooks/use-translation';
import useUnreadMessagesCount from '../../../hooks/use-unread-messages-count';

const SITENAV_APPLICATION_ID = 'client-account';
const MESSAGES_CRITERIA: FeatureAccessOptions = { flag: FLAGS.unreadMessagesIcon };

interface SettingsPageLayoutProps extends PropsWithChildren<unknown> {
  user?: AuthenticatedUser;
  withNav?: boolean;
}

function SettingsPageLayout({ children, user, withNav = true }: SettingsPageLayoutProps) {
  usePageViewTracking();

  const navSlug = useNavSlug();
  const { debouncedTrackMenuClick, debouncedTrackMenuHover } = useDebounceTrackMenu({ navSlug });
  const { translate } = useTranslation('settings-page-layout', { include: translations.DASHBOARD });
  const activeLearner = useActiveLearner(user);

  const { hasAccess: isVt4sStudent } = useIsVt4sStudent();
  const { hasAccess: hasMessagesAccess } = useFeatureAccess({ criteria: MESSAGES_CRITERIA });
  const { data: unreadMessagesCount } = useUnreadMessagesCount({
    enabled: Boolean(hasMessagesAccess),
  });
  const showMessages = Boolean(activeLearner && hasMessagesAccess && !isVt4sStudent);

  const error = useRouteError();

  return (
    <>
      <ScrollRestoration />

      <SiteNav
        activeLearner={activeLearner}
        applicationId={SITENAV_APPLICATION_ID}
        applicationLinkComponent={NavLink}
        baseUrl={process.env.VITE_WWW_BASE_URL}
        getApplicationLinkComponentProps={(item) => ({
          to: item.href ? removeBasePath(item.href, '/client') : '#',
        })}
        navSlug={navSlug}
        onClick={debouncedTrackMenuClick}
        onMouseOver={debouncedTrackMenuHover}
        onSearchSubmit={(query) =>
          (window.location.href = `${process.env.VITE_WWW_BASE_URL}/my-learning/search?q=${query}`)
        }
        openOnHover={true}
        searchBarProps={{
          searchInputProps: {
            placeholder: translate(
              `search_bar_placeholder_${isMobileDevice() ? 'mobile' : 'desktop'}`
            ),
          },
        }}
        unreadMessagesCount={unreadMessagesCount}
        user={user}
        variant={undefined}
        withMessages={showMessages}
        withSearch
      />

      <div className={styles.container}>
        {/* Legacy experience side nav */}
        {withNav && !error ? (
          <div className={styles.pageNav}>
            <SettingsNavPanel />
          </div>
        ) : null}

        <div className={clsx(styles.content, styles.contentMargin)}>
          <Suspense>{children ?? <Outlet />}</Suspense>
        </div>
      </div>
    </>
  );
}

export default SettingsPageLayout;
